import { Helper } from "./helper";
import { KeyColumnDetector } from "./keyColumnDetector";
import { COLORS } from "./constants";

export class SheetComparator {
  private state: any;
  private helper: Helper;
  private config: {
    dataBatchSize: number;
    styleBatchSize: number;
    chunkSize: number;
  };

  constructor(state: any) {
    this.state = state;
    this.helper = new Helper(this.state);
    this.config = this.detectOptimalBatchSizes();
  }

  // Pre-compile regex patterns for better performance
  private compiledPatterns = {
    whitespace: /\s+/g,
    trimPattern: /^\s+|\s+$/g,
  };

  private detectOptimalBatchSizes() {
    // Detect system capabilities and set optimal batch sizes
    const memoryInfo = (performance as any).memory;
    const isLowMemory = memoryInfo && memoryInfo.jsHeapSizeLimit < 2147483648; // 2GB
    
    // Increase batch sizes for better performance
    return {
      dataBatchSize: isLowMemory ? 1000 : 2000,  // Doubled
      styleBatchSize: isLowMemory ? 2000 : 5000, // Increased significantly
      chunkSize: isLowMemory ? 5000 : 10000,     // Doubled
    };
  }

  // Cache for lowercase string conversions to improve performance
  private lowercaseCache = new Map<string, string>();
  // Cache for composite keys to reduce string concatenation
  private keyCache = new Map<string, string>();

  private toLowerCached(str: string): string {
    if (str === null || str === undefined) return "";
    const strValue = String(str);
    let cached = this.lowercaseCache.get(strValue);
    if (cached === undefined) {
      cached = strValue.toLowerCase();
      // Limit cache size to prevent memory issues
      if (this.lowercaseCache.size > 10000) {
        this.lowercaseCache.clear();
      }
      this.lowercaseCache.set(strValue, cached);
    }
    return cached;
  }

  // Optimized key generation with caching
  private generateCompositeKey(values: string[]): string {
    const keyBase = values.join("|");
    let cached = this.keyCache.get(keyBase);
    if (cached === undefined) {
      cached = keyBase;
      if (this.keyCache.size > 5000) {
        this.keyCache.clear();
      }
      this.keyCache.set(keyBase, cached);
    }
    return cached;
  }

  public async compareSheets(oldSheet, newSheet, keyColumnNames, ignoreColumnNames) {
    // Early exit for empty sheets
    if (!oldSheet?.length || !newSheet?.length) {
      return { data: [["Status", "Result"]], styles: {}, stats: { new: 0, updated: 0, deleted: 0, match: 0 } };
    }

    const oldSheetMaxLength = Math.min(this.state.rowLimit || oldSheet.length, oldSheet.length);
    const newSheetMaxLength = Math.min(this.state.rowLimit || newSheet.length, newSheet.length);

    // Get headers
    const oldHeaders = oldSheet[0] || [];
    const newHeaders = newSheet[0] || [];

    // Create hash maps for headers for O(1) lookup - case insensitive
    const headersOldMap = {};
    const headersNewMap = {};
    const headersOldOriginalMap = {}; // Store original header values
    const headersNewOriginalMap = {}; // Store original header values
    const combinedHeadersSet = new Set();
    const combinedHeaders = [];
    const combinedHeadersLower = []; // Store lowercase versions for consistent lookup

    // Add all newHeaders in order first
    newHeaders.forEach((header, index) => {
      if (header !== null && header !== undefined && String(header).trim()) {
        const headerStr = String(header);
        const headerLower = this.toLowerCached(headerStr);
        headersNewMap[headerLower] = index;
        headersNewOriginalMap[headerLower] = headerStr; // Store original value
        if (!combinedHeadersSet.has(headerLower)) {
          combinedHeadersSet.add(headerLower);
          combinedHeaders.push(headerStr); // Use original value for display
          combinedHeadersLower.push(headerLower); // Store lowercase for lookup
        }
      }
    });
    // Then add any oldHeaders not present in newHeaders
    oldHeaders.forEach((header, index) => {
      if (header !== null && header !== undefined && String(header).trim()) {
        const headerStr = String(header);
        const headerLower = this.toLowerCached(headerStr);
        if (!combinedHeadersSet.has(headerLower)) {
          headersOldMap[headerLower] = index;
          headersOldOriginalMap[headerLower] = headerStr; // Store original value
          combinedHeadersSet.add(headerLower);
          combinedHeaders.push(headerStr); // Use original value for display
          combinedHeadersLower.push(headerLower); // Store lowercase for lookup
        } else {
          headersOldMap[headerLower] = index;
          headersOldOriginalMap[headerLower] = headerStr; // Store original value
        }
      }
    });

    // Convert column names to indices for both old and new sheets - case insensitive
    const keyIndicesOld = keyColumnNames
      .map((colName) => {
        const trimmedCol = this.toLowerCached(colName.trim());
        return headersOldMap[trimmedCol];
      })
      .filter((index) => index !== undefined);

    const keyIndicesNew = keyColumnNames
      .map((colName) => {
        const trimmedCol = this.toLowerCached(colName.trim());
        return headersNewMap[trimmedCol];
      })
      .filter((index) => index !== undefined);

    const ignoreIndicesOld = ignoreColumnNames
      .map((colName) => {
        const trimmedCol = this.toLowerCached(colName.trim());
        return headersOldMap[trimmedCol];
      })
      .filter((index) => index !== undefined);

    const ignoreIndicesNew = ignoreColumnNames
      .map((colName) => {
        const trimmedCol = this.toLowerCached(colName.trim());
        return headersNewMap[trimmedCol];
      })
      .filter((index) => index !== undefined);

    // Create output array with headers row
    const outputData = [["Status", ...combinedHeaders]];
    let cellStyles = {}; // Store style information for cells

    // Build dictionary of old data keys for lookup
    const oldDataDict = {};
    const CHUNK_SIZE = this.config.chunkSize; // Process old data in chunks to reduce memory usage

    for (let chunkStart = 1; chunkStart < oldSheetMaxLength; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, oldSheetMaxLength);

      for (let i = chunkStart; i < chunkEnd; i++) {
        const row = oldSheet[i];
        if (!row) continue;

        // Create composite key from key columns using old sheet indices
        const keyValues = keyIndicesOld.map((index) => {
          return row[index] !== undefined ? this.toLowerCached(String(row[index])) : "";
        });
        const key = this.generateCompositeKey(keyValues);

        if (key.trim()) {
          oldDataDict[key] = i;
        }
      }

      // Force garbage collection if available
      if (global.gc) {
        global.gc();
      }
    }

    // Stats for reporting
    const stats = {
      new: 0,
      updated: 0,
      deleted: 0,
      match: 0,
    };

    // Helper function to check if a header is in ignore columns
    const isHeaderIgnored = (header, headerIndex) => {
      const headerLower =
        headerIndex !== undefined
          ? combinedHeadersLower[headerIndex]
          : this.toLowerCached(String(header));
      const oldIndex = headersOldMap[headerLower];
      const newIndex = headersNewMap[headerLower];

      // Check if either the old or new column index is in the excluded list
      return (
        (oldIndex !== undefined && ignoreIndicesOld.includes(oldIndex)) ||
        (newIndex !== undefined && ignoreIndicesNew.includes(newIndex))
      );
    };

    // Helper function to get the correct value for a column
    const getColumnValue = (header, oldRow, newRow, status = "Match", isExistingRecord = true) => {
      const headerLower = this.toLowerCached(String(header));
      const oldIndex = headersOldMap[headerLower];
      const newIndex = headersNewMap[headerLower];

      // ALWAYS use old value for deleted rows
      if (!Object.prototype.hasOwnProperty.call(headersNewMap, headerLower) && oldRow !== null) {
        if (status === "Updated_NEW") {
          return "";
        }
        // Return the original value without date conversion
        return oldRow[oldIndex];
      }

      // For Updated_OLD status, always use old value if available
      if (status === "Updated_OLD") {
        return oldIndex !== undefined ? oldRow[oldIndex] : "";
      }

      // If this column is excluded, always use new data (except for deleted rows handled above)
      if (isHeaderIgnored(header, undefined)) {
        return newIndex !== undefined ? newRow[newIndex] : "";
      }

      if (status === "Updated_NEW") {
        // Always use new value for Updated_NEW
        return newIndex !== undefined ? newRow[newIndex] : "";
      }

      // For existing records, prefer old data but use new if column is new
      if (isExistingRecord) {
        if (oldIndex !== undefined) {
          return oldRow[oldIndex];
        } else if (newIndex !== undefined) {
          return newRow[newIndex];
        }
      } else {
        // For new records, use new data
        return newIndex !== undefined ? newRow[newIndex] : "";
      }

      return "";
    };

    // Process new data in chunks
    for (let chunkStart = 1; chunkStart < newSheetMaxLength; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, newSheetMaxLength);

      for (let i = chunkStart; i < chunkEnd; i++) {
        const newRow = newSheet[i];
        if (!newRow) continue;

        // Create key for this row
        const keyValues = keyIndicesNew.map((index) => {
          return newRow[index] !== undefined ? this.toLowerCached(String(newRow[index])) : "";
        });
        const key = this.generateCompositeKey(keyValues);

        if (!key.trim()) continue;

        if (!Object.prototype.hasOwnProperty.call(oldDataDict, key)) {
          // NEW RECORD
          stats.new++;
          const outputRow = ["New"];

          // Fill with values from new data
          combinedHeaders.forEach((header, j) => {
            outputRow[j + 1] = getColumnValue(header, null, newRow, "New", false);
          });

          outputData.push(outputRow);

          // Set styles - green background for new rows
          for (let j = 0; j <= combinedHeaders.length; j++) {
            this.helper.setCellStyle(cellStyles, outputData.length - 1, j, { bgColor: COLORS.NEW });
          }
        } else {
          // EXISTING RECORD
          const oldRowIndex = oldDataDict[key];
          const oldRow = oldSheet[oldRowIndex];
          let updated = false;

          // Check if any values were updated (excluding key and excluded columns)
          // Use pre-computed header indices for faster lookup
          const headerCount = combinedHeaders.length;
          for (let j = 0; j < headerCount; j++) {
            const headerLower = combinedHeadersLower[j];
            const oldIndex = headersOldMap[headerLower];
            const newIndex = headersNewMap[headerLower];

            // Skip excluded columns and missing columns
            if (isHeaderIgnored(null, j) || oldIndex === undefined || newIndex === undefined) {
              continue;
            }

            // Quick reference equality check first
            const oldRawVal = oldRow[oldIndex];
            const newRawVal = newRow[newIndex];
            
            if (oldRawVal === newRawVal) {
              continue; // Values are identical, no need for string conversion
            }

            const oldVal = this.toLowerCached(String(oldRawVal || ""));
            const newVal = this.toLowerCached(String(newRawVal || ""));

            if (oldVal !== newVal) {
              updated = true;
              break;
            }
          }

          if (updated) {
            // UPDATED RECORD
            stats.updated++;

            // Add OLD version
            const oldOutputRow = ["Updated_OLD"];
            combinedHeaders.forEach((header, j) => {
              // For excluded columns, use new data even in OLD version
              if (isHeaderIgnored(header, j)) {
                oldOutputRow[j + 1] = getColumnValue(header, oldRow, newRow, "Updated_OLD");
              } else {
                oldOutputRow[j + 1] = getColumnValue(header, oldRow, newRow, "Updated_OLD");
              }
            });
            outputData.push(oldOutputRow);
            // Set yellow background for Updated_OLD status cell
            this.helper.setCellStyle(cellStyles, outputData.length - 1, 0, {
              bgColor: COLORS.MODIFIED,
            });

            // Add NEW version
            const newOutputRow = ["Updated_NEW"];
            combinedHeaders.forEach((header, j) => {
              newOutputRow[j + 1] = getColumnValue(header, oldRow, newRow, "Updated_NEW");
            });
            outputData.push(newOutputRow);
            // Set yellow background for Updated_NEW status cell
            this.helper.setCellStyle(cellStyles, outputData.length - 1, 0, {
              bgColor: COLORS.MODIFIED,
            });

            // Highlight changed cells (but not excluded columns)
            for (let j = 0; j < combinedHeaders.length; j++) {
              const header = combinedHeaders[j];
              const headerLower = combinedHeadersLower[j];
              const oldIndex = headersOldMap[headerLower];
              const newIndex = headersNewMap[headerLower];

              if (isHeaderIgnored(header, j)) continue;

              // Use cached raw value comparison first
              const oldRawVal = oldRow[oldIndex];
              const newRawVal = newRow[newIndex];

              if (oldRawVal !== newRawVal) {
                const oldVal = this.toLowerCached(String(oldRawVal || ""));
                const newVal = this.toLowerCached(String(newRawVal || ""));

                if (oldVal !== newVal) {
                  this.helper.setCellStyle(cellStyles, outputData.length - 2, j + 1, {
                    bgColor: COLORS.MODIFIED,
                  });
                  this.helper.setCellStyle(cellStyles, outputData.length - 1, j + 1, {
                    bgColor: COLORS.MODIFIED,
                  });
                }
              }
            }
          } else {
            // MATCHING RECORD
            stats.match++;
            const outputRow = ["Match"];

            combinedHeaders.forEach((header, j) => {
              outputRow[j + 1] = getColumnValue(header, oldRow, newRow, "Match", true);

              // Highlight cells from new columns (but not excluded columns)
              const headerLower = combinedHeadersLower[j];
              if (!(headerLower in headersOldMap) && !isHeaderIgnored(header, j)) {
                this.helper.setCellStyle(cellStyles, outputData.length, j + 1, {
                  bgColor: COLORS.NEW,
                });
              }
            });

            outputData.push(outputRow);
          }

          // Remove from dictionary to track deleted records
          delete oldDataDict[key];
        }
      }

      // Force garbage collection if available
      if (global.gc) {
        global.gc();
      }
    }

    // Process deleted records
    for (const key in oldDataDict) {
      stats.deleted++;
      const oldRowIndex = oldDataDict[key];
      const oldRow = oldSheet[oldRowIndex];

      const outputRow = ["Deleted"];
      combinedHeaders.forEach((header, j) => {
        // For deleted records, we don't have new data, so use old data even for excluded columns
        const headerLower = combinedHeadersLower[j];
        const oldIndex = headersOldMap[headerLower];
        // Preserve original values without date conversion
        outputRow[j + 1] = oldIndex !== undefined ? oldRow[oldIndex] : "";
      });

      outputData.push(outputRow);

      // Set styles - red for deleted rows
      for (let j = 0; j < outputRow.length; j++) {
        this.helper.setCellStyle(cellStyles, outputData.length - 1, j, { bgColor: COLORS.DELETED });
      }
    }

    // Add header styles
    // Set blue background for Status cell
    this.helper.setCellStyle(cellStyles, 0, 0, { bgColor: "#0078D4" }); // Blue background for Status cell

    combinedHeaders.forEach((header, j) => {
      // Highlight new or deleted columns in the header
      const headerLower = combinedHeadersLower[j];
      if (!(headerLower in headersOldMap)) {
        this.helper.setCellStyle(cellStyles, 0, j + 1, { bgColor: COLORS.NEW }); // New column - green
      } else if (!(headerLower in headersNewMap)) {
        this.helper.setCellStyle(cellStyles, 0, j + 1, { bgColor: COLORS.DELETED }); // Deleted column - red
      }
    });

    // Highlight all cells in new or deleted columns
    combinedHeaders.forEach((header, j) => {
      const headerLower = combinedHeadersLower[j];
      if (!(headerLower in headersOldMap)) {
        // New column: highlight all cells in this column (except header)
        for (let i = 1; i < outputData.length; i++) {
          this.helper.setCellStyle(cellStyles, i, j + 1, { bgColor: COLORS.NEW });
        }
      } else if (!(headerLower in headersNewMap)) {
        // Deleted column: highlight all cells in this column (except header)
        for (let i = 1; i < outputData.length; i++) {
          this.helper.setCellStyle(cellStyles, i, j + 1, { bgColor: COLORS.DELETED });
        }
      }
    });

    return {
      data: outputData,
      styles: cellStyles,
      stats: stats,
    };
  }

  public async processComparison(oldData, newData) {
    const sheetName = "Comparison Results";

    // Check if datasets are extremely large and need streaming processing
    const isExtremelyLarge = oldData.length > 50000 || newData.length > 50000;

    if (isExtremelyLarge) {
      this.helper.showWarning({
        message:
          "Detected very large datasets. Using optimized processing mode for better performance.",
        cleanUp: false,
      });
    }

    const keyColumnDetector = new KeyColumnDetector(this.state);

    const columnMetrics = await keyColumnDetector.detectKeyColumns(newData, oldData);
    if ("error" in columnMetrics) {
      this.helper.showError({ message: columnMetrics.error });
      return;
    }

    // Convert suggested column indices to names
    const suggestedKeyColumns = columnMetrics.suggestedKeyColumns;
    //const suggestedIgnoreColumns = columnMetrics.unchangedColumns;
    const commonHeaders = columnMetrics.commonHeaders;

    console.log("==========columnMetrics==========");
    console.log(columnMetrics);

    const keyColNames = (await this.helper.promptUser({
      suggestedValues: suggestedKeyColumns,
      requiredInput: true,
      type: "KeyColumns",
      possibleValues: commonHeaders,
    })) as string;
    if (!keyColNames) return; // User cancelled
    const ignoreColNames = (await this.helper.promptUser({
      suggestedValues: [],
      requiredInput: false,
      type: "ExcludedColumns",
      possibleValues: commonHeaders,
    })) as string;

    // Convert column names to arrays
    const keyColumnNames = keyColNames.split(",").map((col) => col.trim());
    const ignoreColumnNames = ignoreColNames
      ? ignoreColNames.split(",").map((col) => col.trim())
      : [];

    const comparisonData = await this.compareSheets(
      oldData,
      newData,
      keyColumnNames,
      ignoreColumnNames
    );

    // Create a status message
    this.helper.showSuccess({
      message: `Processed sheet: ${sheetName} - Found ${comparisonData.stats.new} new, ${comparisonData.stats.updated} updated, ${comparisonData.stats.deleted} deleted, ${comparisonData.stats.match} matching records`,
    });

    // Insert data into active worksheet with batch processing
    await this.writeDataInBatches(comparisonData, sheetName, keyColNames, ignoreColNames);
  }

  private async writeDataInBatches(comparisonData, sheetName, keyColNames, ignoreColNames) {
    const BATCH_SIZE = this.config.dataBatchSize;
    const totalRows = comparisonData.data.length;
    const totalCols = comparisonData.data[0].length;

    await Excel.run(async (context) => {
      // Delete all existing worksheets
      const worksheets = context.workbook.worksheets;
      worksheets.load("items");
      await context.sync();

      // Check if this looks like a new workbook (only has default Sheet1)
      const isNewWorkbook = worksheets.items.length === 1 && worksheets.items[0].name === "Sheet1";

      const firstSheet = worksheets.items[0];
      let comparisonSheet = null;
      let summarySheet = null;

      // Delete existing comparison and summary sheets if they exist
      for (let i = worksheets.items.length - 1; i >= 0; i--) {
        const worksheet = worksheets.items[i];
        if (worksheet.name === sheetName) {
          // clear the worksheet
          const usedRange = worksheet.getUsedRange();
          usedRange.clear();
          comparisonSheet = worksheet;
          comparisonSheet.activate();
        } else if (worksheet.name === "Summary of Changes") {
          // clear the worksheet
          const usedRange = worksheet.getUsedRange();
          usedRange.clear();
          summarySheet = worksheet;
        }
      }

      if (isNewWorkbook) {
        // Delete all worksheets except the first one (we need at least one to exist)
        for (let i = worksheets.items.length - 1; i > 0; i--) {
          worksheets.items[i].delete();
        }

        // Rename the first worksheet to our comparison sheet name
        firstSheet.name = sheetName;
        comparisonSheet = firstSheet;
        comparisonSheet.activate();
      }

      if (comparisonSheet === null) {
        comparisonSheet = context.workbook.worksheets.add(sheetName);
        comparisonSheet.activate();
      }

      if (summarySheet === null) {
        summarySheet = context.workbook.worksheets.add("Summary of Changes");
      }

      await context.sync();
    });

    // Write data in batches
    for (let startRow = 0; startRow < totalRows; startRow += BATCH_SIZE) {
      const endRow = Math.min(startRow + BATCH_SIZE, totalRows);
      const batchData = comparisonData.data.slice(startRow, endRow);

      await Excel.run(async (context) => {
        const comparisonSheet = context.workbook.worksheets.getItem(sheetName);
        const range = comparisonSheet.getRangeByIndexes(startRow, 0, endRow - startRow, totalCols);
        range.values = batchData;
      });

      // Show progress for large datasets
      if (totalRows > 5000) {
        const progress = Math.round((endRow / totalRows) * 100);
        this.helper.showSuccess({
          message: `Writing data: ${progress}% complete`,
          cleanUp: false,
        });
      }
    }

    // Apply formatting using optimized batch approach
    if (comparisonData.styles) {
      await this.applyFormattingOptimized(comparisonData.styles, sheetName);
    }

    // Write summary data
    await Excel.run(async (context) => {
      const summarySheet = context.workbook.worksheets.getItem("Summary of Changes");

      // Add statistics to summary sheet
      const summaryData = [
        ["Comparison Summary", ""],
        ["Generated:", new Date().toLocaleString()],
        ["", ""],
        ["Files used:", ""],
        ["Old File:", this.state.oldFileName],
        ["New File:", this.state.newFileName],
        ["", ""],
        ["Sheets used:", ""],
        ["Old Sheet:", this.state.oldSheet],
        ["New Sheet:", this.state.newSheet],
        ["", ""],
        ["Key Columns:", keyColNames],
        ["Excluded Columns:", ignoreColNames],
        ["", ""],
        ["Category", "Count"],
        ["New Records", comparisonData.stats.new],
        ["Updated Records", comparisonData.stats.updated],
        ["Deleted Records", comparisonData.stats.deleted],
        ["Matching Records", comparisonData.stats.match],
        ["", ""],
        [
          "Total Records",
          comparisonData.stats.new +
            comparisonData.stats.updated +
            comparisonData.stats.deleted +
            comparisonData.stats.match,
        ],
      ];

      // Write summary data to the Summary of Changes worksheet
      const rowCount = summaryData.length;
      const colCount = summaryData[0].length;
      const summaryRange = summarySheet.getRangeByIndexes(0, 0, rowCount, colCount);
      summaryRange.values = summaryData;
      summaryRange.format.autofitColumns();

      summarySheet.getRange("A2:B2").format.font.bold = true;
      summarySheet.getRange("A12:B12").format.font.bold = true;
      summarySheet.getRange("A13:B13").format.font.bold = true;
      summarySheet.getRange("A15:B15").format.font.bold = true;
      summarySheet.getRange("A21:B21").format.font.bold = true;

      // Make title rows bold (rows where second column is empty)
      for (let i = 0; i < rowCount; i++) {
        if (summaryData[i][1] === "") {
          summarySheet.getRange(`A${i + 1}:B${i + 1}`).format.font.bold = true;
        }
      }

      // Add background colors to the category rows
      summarySheet.getRange("A16:B16").format.fill.color = COLORS.NEW; // New Records
      summarySheet.getRange("A17:B17").format.fill.color = COLORS.MODIFIED; // Updated Records
      summarySheet.getRange("A18:B18").format.fill.color = COLORS.DELETED; // Deleted Records
      summarySheet.getRange("A19:B19").format.fill.color = "#FFFFFF"; // Matching Records (white)

      await context.sync();
    });

    this.helper.showSuccess({
      message: `✅ Comparison complete!<br>Close the Task Pane to run again.`,
      cleanUp: false,
    });
  }

  private async applyFormattingOptimized(styles, sheetName) {
    // Group styles by color to minimize API calls
    const stylesByColor = {};

    for (const cellKey in styles) {
      const style = styles[cellKey];
      if (style.bgColor) {
        if (!stylesByColor[style.bgColor]) {
          stylesByColor[style.bgColor] = [];
        }
        stylesByColor[style.bgColor].push(cellKey);
      }
    }

    // Apply formatting by color groups - process all colors in parallel batches
    const colorKeys = Object.keys(stylesByColor);
    const FORMAT_BATCH_SIZE = Math.max(this.config.styleBatchSize, 2000); // Increase batch size for better performance

    // Process all colors in larger batches to reduce Excel.run calls
    for (let colorIndex = 0; colorIndex < colorKeys.length; colorIndex++) {
      const color = colorKeys[colorIndex];
      const cellKeys = stylesByColor[color];

      // Use larger batches and better range grouping
      for (let i = 0; i < cellKeys.length; i += FORMAT_BATCH_SIZE) {
        const batchKeys = cellKeys.slice(i, i + FORMAT_BATCH_SIZE);

        await Excel.run(async (context) => {
          const comparisonSheet = context.workbook.worksheets.getItem(sheetName);

          // Use improved range grouping algorithm
          const ranges = this.groupAdjacentCellsOptimized(batchKeys);

          // Apply formatting to all ranges in one batch - more efficient approach
          if (ranges.length > 0) {
            // If we have many small ranges, try to combine them into larger ranges
            const optimizedRanges = this.optimizeRanges(ranges);

            for (const rangeInfo of optimizedRanges) {
              const range = comparisonSheet.getRangeByIndexes(
                rangeInfo.startRow,
                rangeInfo.startCol,
                rangeInfo.rowCount,
                rangeInfo.colCount
              );
              range.format.fill.color = color;
            }
          }
        });

        // Show progress for large datasets
        if (cellKeys.length > 500) {
          const progress = Math.round(((i + FORMAT_BATCH_SIZE) / cellKeys.length) * 100);
          const colorDescription = this.getColorDescription(color);
          this.helper.showSuccess({
            message: `Applying formatting (${colorDescription}): ${Math.min(progress, 100)}% complete`,
            cleanUp: false,
          });
        }
      }
    }
  }

  private optimizeRanges(ranges) {
    if (ranges.length <= 1) return ranges;

    // Sort ranges by start position
    const sortedRanges = ranges.sort((a, b) => {
      if (a.startRow !== b.startRow) return a.startRow - b.startRow;
      return a.startCol - b.startCol;
    });

    const optimizedRanges = [];
    let currentRange = { ...sortedRanges[0] };

    for (let i = 1; i < sortedRanges.length; i++) {
      const nextRange = sortedRanges[i];

      // Check if ranges can be merged (same row and adjacent columns, or same column and adjacent rows)
      const canMergeRows =
        currentRange.startCol === nextRange.startCol &&
        currentRange.colCount === nextRange.colCount &&
        currentRange.startRow + currentRange.rowCount === nextRange.startRow;

      const canMergeCols =
        currentRange.startRow === nextRange.startRow &&
        currentRange.rowCount === nextRange.rowCount &&
        currentRange.startCol + currentRange.colCount === nextRange.startCol;

      if (canMergeRows) {
        // Merge vertically
        currentRange.rowCount += nextRange.rowCount;
      } else if (canMergeCols) {
        // Merge horizontally
        currentRange.colCount += nextRange.colCount;
      } else {
        // Cannot merge, save current range and start new one
        optimizedRanges.push(currentRange);
        currentRange = { ...nextRange };
      }
    }

    // Add the last range
    optimizedRanges.push(currentRange);

    return optimizedRanges;
  }

  private groupAdjacentCellsOptimized(cellKeys) {
    if (cellKeys.length === 0) return [];

    // Convert cell keys to row/col pairs and sort them
    const cells = cellKeys
      .map((key) => {
        const [row, col] = key.split(",").map(Number);
        return { row, col };
      })
      .sort((a, b) => {
        // Sort by row first, then by column
        if (a.row !== b.row) return a.row - b.row;
        return a.col - b.col;
      });

    const ranges = [];
    let currentRange = null;

    for (const cell of cells) {
      if (!currentRange) {
        // Start new range
        currentRange = {
          startRow: cell.row,
          startCol: cell.col,
          rowCount: 1,
          colCount: 1,
        };
      } else {
        // Check if this cell is adjacent to current range
        const isAdjacentRow = cell.row === currentRange.startRow + currentRange.rowCount;
        const isAdjacentCol = cell.col === currentRange.startCol + currentRange.colCount;
        const isSameRow = cell.row === currentRange.startRow;
        const isSameCol = cell.col === currentRange.startCol;

        if ((isAdjacentRow && isSameCol) || (isAdjacentCol && isSameRow)) {
          // Extend current range
          if (isAdjacentRow) {
            currentRange.rowCount++;
          } else {
            currentRange.colCount++;
          }
        } else {
          // Save current range and start new one
          ranges.push(currentRange);
          currentRange = {
            startRow: cell.row,
            startCol: cell.col,
            rowCount: 1,
            colCount: 1,
          };
        }
      }
    }

    // Add the last range
    if (currentRange) {
      ranges.push(currentRange);
    }

    return ranges;
  }


  private getColorDescription(color: string) {
    if (color === COLORS.NEW) {
      return "new records";
    } else if (color === COLORS.MODIFIED) {
      return "updated records";
    } else if (color === COLORS.DELETED) {
      return "deleted records";
    } else if (color === "#0078D4") {
      return "status column";
    } else if (color === "#FFFFFF") {
      return "matching records";
    }
    return "other formatting"; // Fallback for other colors
  }
}
